import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CriminalActivityHit, eActivitySubType } from 'projects/reg-hub-common/src/public-api';
import { ConsortiumService, DecodedSerialNumberInformation, eActivityType, FadeInOutAnimation, SlideDownOnlyAnimation, TimelineEvent, Utilities } from 'reg-hub-common';
import { catchError, concat, forkJoin, last, of, Subject, switchMap, tap, timeout, timer } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from 'reg-hub-common';

export interface Bubble {
  x: string;
  xValue: number;
  events: TimelineEvent[];
}

@Component({
    selector: 'app-vinsights',
    templateUrl: './vinsights.component.html',
    styleUrl: './vinsights.component.css',
    animations: [
        SlideDownOnlyAnimation,
        FadeInOutAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VinsightsComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  serialNumber = '';
  loading = false;
  hasData = false;

  criminalActivityResults: CriminalActivityHit[] | undefined;
  decodedInformation: DecodedSerialNumberInformation | undefined;
  timeline: TimelineEvent[] = [];
  bubbles: Bubble[] = [];

  selectedBubble: Bubble | undefined;
  hoveredBubble: Bubble | undefined;

  private onDestroy$ = new Subject<void>();

  constructor(
    private consortiumService: ConsortiumService,
    private datePipe: DatePipe,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    const serialFromUrl = this.route.snapshot.queryParamMap.get('serialNumber');

    if (serialFromUrl) {
      this.serialNumber = serialFromUrl;

      // Optional: auto-trigger the search
      this.onGetVINsightsClick();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onGetVINsightsClick() {
    this.loading = true;
    this.hasData = false;
    this.selectedBubble = undefined;
    this.hoveredBubble = undefined;
    this.timeline = [];
    this.bubbles = [];

    //attempts crime check, times out after 2.5 seconds
    const crimCheckObservable = this.consortiumService.performCriminalActivityCheck(this.serialNumber).pipe(
      timeout({
        first: 2500,
        with: () => of([])
      }),
      catchError(() => of([])),
      tap(crimCheck => this.criminalActivityResults = crimCheck));
    //gets timeline and decoded serial number information
    const dataObservable = forkJoin([
        this.consortiumService.getDecodedSerialNumberInformation(this.serialNumber),
        this.consortiumService.getSerialNumberTimeline(this.serialNumber)
      ]).pipe(tap(data => {
        this.decodedInformation = data[0];
        this.timeline = data[1];
        this.hasData = true;
        this.computeBubbles(this.timeline);
      }));

    //attempts crime check first, which times out after 2.5s. should not show an error message due to
    //error handling in pipe of that observable. if it times out or succeeds, carries on with the
    //data observable regardless
    concat(crimCheckObservable, dataObservable).subscribe({
      next: () => this.loading = false,
      error: () => this.loading = false,
      complete: () => this.loading = false
    });
  }

  isCrimeCheckClear() {
    return (this.criminalActivityResults?.length ?? 0) === 0;
  }

  computeBubbles(events: TimelineEvent[]) {
    const xValues = events.map(e => this.computeBubbleX(e));
    this.bubbles = [];
    const minDiff = 2;

    let i = 0;
    let j = 0;

    while (i < xValues.length && j < xValues.length) {
      const bubble = {
        x: `${xValues[i]}%`,
        xValue: xValues[i],
        events: [events[i]],
        shouldShowEventCount: false
      }

      j = i + 1;

      for (; j < xValues.length; j++) {
        const diff = xValues[j] - xValues[i];

        if (diff < minDiff) {
          bubble.events.push(events[j]);
        } else {
          i = j - 1;
          break;
        }
      }

      i++;
      this.bubbles.push(bubble);
    }

    if (this.bubbles.length === 1) {
      this.bubbles.at(0)!.x = '50%';
      this.bubbles.at(0)!.xValue = 50;
    }
  }

  computeBubbleX(event: TimelineEvent) {
    const startTime = new Date(this.timeline.at(0)!.activityDate!).getTime();
    const endTime = new Date(this.timeline.at(this.timeline.length - 1)!.activityDate!).getTime();
    let eventTime = new Date(event.activityDate!).getTime();

    let totalTime = endTime - startTime;

    if (totalTime <= 1) {
      totalTime = 1;
    }

    eventTime = endTime - eventTime;
    const timePercentage = 0.1 + ((1 - (eventTime / totalTime)) * 0.8);

    return timePercentage * 100;
  }

  getBubbleXOffset(bubble: Bubble) {
    if (bubble.xValue > 50) {
      return { 'right': `calc(${100 - bubble.xValue}% - 25px` };
    } else {
      return { 'left': `calc(${bubble.x} - 25px` };
    }
  }

  onBubbleClick(bubble: Bubble) {
    if (this.selectedBubble === bubble) {
      this.selectedBubble = undefined;
    } else {
      this.selectedBubble = bubble;
    }
  }


  formatEvent(event: TimelineEvent) {
    if(event.activityType === eActivityType.CrimeCheckClear) {
      return this.transloco.translate('consortium.events.stolenCheckClear');
    } else if(event.activityType === eActivityType.CrimeCheckHit) {
      return this.transloco.translate('consortium.events.stolenCheckHit');
    } else if(event.activityType === eActivityType.Registration) {
      if (event.activitySubType === eActivitySubType.repairLien) {
        if(event.jurisdiction === 'ON') {
          return this.transloco.translate('consortium.events.registrationRsla');
        } else {
          return this.transloco.translate('consortium.events.registrationRepair');
        }
      } else {
        return this.transloco.translate('consortium.events.registration');
      }
    } else {
      return this.enumLabels.label('activityType', event.activityType);
    }
  }

  onBubbleMouseEnter(bubble: Bubble) {
    this.hoveredBubble = bubble;
  }

  onBubbleMouseLeave(bubble: Bubble) {
    this.hoveredBubble = undefined;

    if (this.selectedBubble !== bubble) {
    }
  }

  getCrimeCheckInformation() {
    const lastHit = (this.criminalActivityResults?.length ?? 0) > 0 ? this.criminalActivityResults![0] : null;

    if(!lastHit) {
      return '';
    } else {
      return `Vehicle was reported stole in ${lastHit.jurisdiction} on or before ${this.datePipe.transform(lastHit.added, 'yyyy-MM-dd')}`;
    }
  }

  showBubbleIcon(bubble: Bubble) {
    const icon = this.getBubbleIcon(bubble);
    return !!icon;
  }

  getBubbleIcon(bubble: Bubble) {
    if (bubble.events.length >= 2 &&
      bubble.events.every(e => e.activityType === eActivityType.Search || e.activityType === eActivityType.NotFound || e.activityType === eActivityType.CrimeCheckClear)) {
      return 'search_check_2';
    } else if (bubble.events.every(e => e.activityType === eActivityType.NotFound)) {
      return 'search_check_2';
    } else if (bubble.events.every(e => e.activityType === eActivityType.Search)) {
      return 'search';
    } else if (bubble.events.every(e => e.activityType === eActivityType.CrimeCheckHit)) {
      return 'siren';
    } else if (bubble.events.every(e => e.activityType === eActivityType.Registration) &&
      bubble.events.every(e => e.activitySubType === eActivitySubType.repairLien)) {
      return 'build';
    } else if (bubble.events.every(e => e.activityType === eActivityType.Registration)) {
      return 'article';
    } else if (bubble.events.every(e => e.activityType === eActivityType.Discharge)) {
      return 'delete_forever';
    } else if (bubble.events.every(e => e.activityType === eActivityType.CrimeCheckClear)) {
      return 'check_circle';
    } else if (bubble.events.every(e => e.activityType === eActivityType.InRepossession)) {
      return 'gavel';
    } else {
      return '';
    }
  }

  isDecodeError() {
    return !this.decodedInformation?.make &&
      !this.decodedInformation?.model &&
      !this.decodedInformation?.year &&
      !this.decodedInformation?.trim &&
      this.decodedInformation?.errorText;
  }

  bubbleHasJurisdiction(bubble: Bubble) {
    return bubble.events.some(e => !!e.jurisdiction);
  }

  shouldShowWarningIcon(event: TimelineEvent) {
    if(event.activityType === eActivityType.NotFound ||
      event.activityType === eActivityType.CrimeCheckClear ||
      event.activityType === eActivityType.CrimeCheckHit) {
      return false;
    } else {
      return !event.corporationID || !event.isRequestingUserCorporation;
    }
  }
}
